/* eslint-disable lingui/no-unlocalized-strings -- Storage keys and hostnames, not UI copy. */
import { type FirstTouch, sanitizeFirstTouch, UTM_KEYS } from "@reactive-resume/utils";

import { type AnalyticsConsent, getConsentChoice, setConsentChoice } from "./consent";

/*
 * First-touch attribution for First Round.
 *
 * - On the first page load in a browser we record the UTMs, the external referrer and the
 *   landing page (sanitised: no query strings, no emails).
 * - firstround.com.au and theresume.io are different origins, so localStorage is not shared. Links
 *   between them (both directions) carry the record, the sending site's Mixpanel device id and the
 *   visitor's Cookie settings choice in a single `ft` query parameter. The receiving site adopts
 *   the first touch if it has none of its own yet, keeps one anonymous visitor across the hop (see
 *   adoptDevice in mixpanel.ts), applies the cookie choice, then removes `ft` from the address bar.
 * - The authenticated analytics context endpoint persists first touch and the latest campaign
 *   against the local user ID. Browser storage and the CRM/Mixpanel records remain available.
 */

const STORAGE_KEY = "first_touch";
const DEVICE_KEY = "first_touch_device";
const CAMPAIGN_KEY = "latest_campaign";
const memory = new Map<string, string>();
export const HANDOFF_PARAM = "ft";

// Referrers from our own sites are navigation, not acquisition.
const OWN_HOSTS = /(^|\.)(firstround\.com\.au|theresume\.io)$/i;

type Handoff = {
  touch: FirstTouch;
  latestCampaign?: FirstTouch;
  device?: string;
  consent?: AnalyticsConsent;
  ga4ClientId?: string;
  issuedAt?: number;
};
let ga4ClientId: string | undefined;
let incomingGa4ClientId: string | undefined;
export const setGa4HandoffId = (id: string) => {
  ga4ClientId = id;
};
export const getIncomingGa4ClientId = () => incomingGa4ClientId;

const read = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
};

const write = (key: string, value: string) => {
  memory.set(key, value);
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode / storage disabled: attribution is best effort.
  }
};

const encode = (value: Handoff) =>
  btoa(unescape(encodeURIComponent(JSON.stringify(value))))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const decode = (value: string): Handoff | null => {
  try {
    const json = decodeURIComponent(escape(atob(value.replace(/-/g, "+").replace(/_/g, "/"))));
    if (value.length > 6000) return null;
    const parsed = JSON.parse(json) as {
      touch?: unknown;
      latestCampaign?: unknown;
      device?: unknown;
      consent?: unknown;
      ga4ClientId?: unknown;
      issuedAt?: unknown;
    };
    const touch = sanitizeFirstTouch(parsed.touch);
    if (!touch) return null;
    const device =
      typeof parsed.device === "string" && /^[\w$:.-]{1,100}$/.test(parsed.device)
        ? parsed.device
        : undefined;
    const consent =
      parsed.consent === "granted" || parsed.consent === "denied" ? parsed.consent : undefined;
    // Like Google's linker, browser identity expires quickly. A copied old link must not merge visitors.
    const fresh =
      typeof parsed.issuedAt === "number" &&
      Date.now() - parsed.issuedAt >= -10_000 &&
      Date.now() - parsed.issuedAt <= 120_000;
    const ga4ClientId =
      fresh &&
      typeof parsed.ga4ClientId === "string" &&
      /^\d{1,20}\.\d{1,20}$/.test(parsed.ga4ClientId)
        ? parsed.ga4ClientId
        : undefined;
    return {
      touch,
      latestCampaign: sanitizeFirstTouch(parsed.latestCampaign) ?? undefined,
      device,
      consent,
      ga4ClientId,
    };
  } catch {
    return null;
  }
};

export const getFirstTouch = (): FirstTouch | null => {
  const stored = read(STORAGE_KEY);
  if (!stored) return null;
  try {
    return sanitizeFirstTouch(JSON.parse(stored));
  } catch {
    return null;
  }
};

export const getLatestCampaign = (): FirstTouch | null => {
  try {
    return sanitizeFirstTouch(JSON.parse(read(CAMPAIGN_KEY) ?? "null"));
  } catch {
    return null;
  }
};

const saveLatestCampaign = (touch: FirstTouch | undefined | null) => {
  if (!touch || !UTM_KEYS.some((key) => touch[key])) return;
  const previous = getLatestCampaign();
  if (!previous || touch.captured_at >= previous.captured_at)
    write(CAMPAIGN_KEY, JSON.stringify(touch));
};

// The other site's device id carried on the link that opened this page, if any.
let incomingDevice: string | undefined;

/** The other site's Mixpanel device id, from the link that opened this page (see adoptDevice). */
export const getIncomingDevice = () => incomingDevice;

/** The last device id handed over from the other site, kept for linking after sign-in. */
export const getHandedOffDevice = () => read(DEVICE_KEY) ?? undefined;

const fromCurrentPage = (): FirstTouch | null => {
  const params = new URLSearchParams(window.location.search);
  const raw: Record<string, unknown> = {
    captured_at: new Date().toISOString(),
    landing_page: window.location.pathname,
    landing_host: window.location.host,
  };
  for (const key of UTM_KEYS) raw[key] = params.get(key) ?? undefined;
  try {
    if (document.referrer && !OWN_HOSTS.test(new URL(document.referrer).hostname)) {
      raw.referrer = document.referrer;
    }
  } catch {
    // Malformed referrer: ignore.
  }
  return sanitizeFirstTouch(raw);
};

/**
 * Run once at startup, before anything renders links. Returns the browser's first touch.
 */
export const captureFirstTouch = (): FirstTouch | null => {
  if (typeof window === "undefined") return null;

  const url = new URL(window.location.href);
  const incoming = url.searchParams.get(HANDOFF_PARAM);
  if (incoming !== null) {
    const handoff = decode(incoming);
    saveLatestCampaign(handoff?.latestCampaign ?? handoff?.touch);
    incomingGa4ClientId = handoff?.consent === "denied" ? undefined : handoff?.ga4ClientId;
    // The marketing site saw the visitor first, so its record wins over this page's.
    if (handoff && !getFirstTouch()) write(STORAGE_KEY, JSON.stringify(handoff.touch));
    if (handoff?.device) {
      incomingDevice = handoff.device;
      write(DEVICE_KEY, handoff.device);
    }
    // Before analytics starts, so an opted-out visitor sends nothing on this site either.
    // A URL must never silently undo an explicit opt-out on this browser origin.
    if (
      handoff?.consent === "denied" ||
      (handoff?.consent === "granted" && getConsentChoice() !== "denied")
    ) {
      setConsentChoice(handoff.consent);
    }
    // Remove only `ft`, leaving the rest of the query exactly as written (e.g. redirect=/x).
    const rest = window.location.search
      .slice(1)
      .split("&")
      .filter((part) => part && !part.startsWith(`${HANDOFF_PARAM}=`));
    const search = rest.length > 0 ? `?${rest.join("&")}` : "";
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${search}${window.location.hash}`,
    );
  }

  const touch = fromCurrentPage();
  saveLatestCampaign(touch);
  const existing = getFirstTouch();
  if (existing) return existing;

  if (touch) write(STORAGE_KEY, JSON.stringify(touch));
  return touch;
};

/** `ft=...` value for a link to the other site, or undefined when there is nothing to carry. */
export const handoffValue = (device?: string): string | undefined => {
  const touch = getFirstTouch();
  return touch
    ? encode({
        touch,
        latestCampaign: getLatestCampaign() ?? undefined,
        device,
        consent: getConsentChoice() ?? undefined,
        ga4ClientId: getConsentChoice() === "denied" ? undefined : ga4ClientId,
        issuedAt: Date.now(),
      })
    : undefined;
};

/** Test hook. */
export const decodeHandoff = decode;

const APP_HOST = "theresume.io";
const FIRST_ROUND_HOSTS = new Set(["firstround.com.au", "www.firstround.com.au"]);

/** Which links get `ft`: from the marketing hosts into the app, and from the app to First Round. */
export const handoffTarget = (fromMarketingHost: boolean) =>
  fromMarketingHost
    ? (hostname: string) => hostname === APP_HOST
    : (hostname: string) => FIRST_ROUND_HOSTS.has(hostname);

/**
 * Add `ft` to any link to the other site at the moment it is used (like GA's linker does for
 * `_gl`), so the handoff carries the current Mixpanel device id and every CTA is covered without
 * each one having to remember.
 */
let decoratorInstalled = false;

export const installHandoffDecorator = (
  getDevice: () => string | undefined,
  isTarget: (hostname: string) => boolean,
) => {
  if (decoratorInstalled) return;
  decoratorInstalled = true;
  const decorate = (event: Event) => {
    const anchor = (event.target as Element | null)?.closest?.("a[href]");
    if (!(anchor instanceof HTMLAnchorElement)) return;
    try {
      const url = new URL(anchor.href);
      if (!isTarget(url.hostname)) return;
      const value = handoffValue(getDevice());
      if (!value) return;
      url.searchParams.set(HANDOFF_PARAM, value);
      anchor.href = url.toString();
    } catch {
      // Not a URL we can decorate.
    }
  };
  // Capture phase and mousedown, so the href is updated before any navigation starts.
  for (const type of ["mousedown", "keydown", "touchstart", "click"]) {
    document.addEventListener(type, decorate, { capture: true, passive: true });
  }
};

/** Programmatic cross-domain redirects do not fire anchor events. */
export const withAttributionHandoff = (href: string) => {
  try {
    const url = new URL(href);
    if (!OWN_HOSTS.test(url.hostname)) return href;
    const value = handoffValue();
    if (value) url.searchParams.set(HANDOFF_PARAM, value);
    return url.toString();
  } catch {
    return href;
  }
};
